import * as ko from "knockout";
import * as Levenshtein from 'fast-levenshtein';
import "./atkins-contact.less"

import { EnquiryFormBindable } from "../../common/forms/enquiryFormBindable";
import BodyTextBindable from "../../BodyTextBindable";
import BrandedPage from "../brandedPage";
import compactSubmitButtonTemplate from "../../templates/horizontalForm/compactSubmitButton.html";
import customerEnquiryFormTemplate from "../../templates/customerEnquiryForm.html";
import FrequentlyAskedQuestion from "./FrequentlyAskedQuestion";
import FrequentlyAskedQuestionData from "./FrequentlyAskedQuestionData";
import selectEnquiryTemplate from "../../templates/horizontalForm/selectEnquiry.html";
import textInputEnquiryTemplate from "../../templates/horizontalForm/textInputEnquiry.html";

import alertTemplate from "../../templates/alert.html";



interface KeyWord {
    value: string;
    weight: number;
}

class ContactAndHelp extends BrandedPage {

    public readonly categories: ko.ObservableArray<Category> = ko.observableArray([]);
    public readonly selectedCategories: ko.Computed<Category[]> = ko.computed(() => this.categories().filter(c => c.selected()));

    public readonly searchText: ko.Observable<string> = ko.observable("");

    public readonly enquiryForm: EnquiryFormBindable = new EnquiryFormBindable();

    private readonly allQuestions: ko.ObservableArray<AskedQuestion> = ko.observableArray([]);
    public readonly visibleQuestions: ko.ObservableArray<AskedQuestion> = ko.observableArray([]);
    public readonly isLoading: ko.Observable<boolean> = ko.observable(true);
    public readonly busy: ko.Observable<boolean> = ko.observable(false);
    public readonly frequentlyAskedQuestionData: FrequentlyAskedQuestionData = new FrequentlyAskedQuestionData();

    private readonly pageSize: number = 20;

    protected async loadContentAsync(): Promise<void> {
        await super.loadContentAsync();

        this.searchText.extend({ rateLimit: { method: "notifyWhenChangesStop", timeout: 600 } });
        this.searchText.subscribe(() => { this._refreshVisibleQuestions() });
        this.selectedCategories.subscribe(() => { this._refreshVisibleQuestions() });
        
        this.categories.removeAll();
        this.frequentlyAskedQuestionData.allCategories.forEach(c => this.categories.push(new Category(c, c)));

        this.allQuestions.removeAll();
        this.frequentlyAskedQuestionData.questions.forEach((q => {
            this.allQuestions.push(new AskedQuestion(q));
        }));

        this.isLoading(false);
        this._refreshVisibleQuestions();
    }

    protected getTemplateHtmlSnippets(): string[] {
        let snippets : string[] = super.getTemplateHtmlSnippets();
        snippets.push(customerEnquiryFormTemplate, compactSubmitButtonTemplate, selectEnquiryTemplate, textInputEnquiryTemplate, alertTemplate);
        return snippets;
    }

    private _refreshVisibleQuestions(): void {
        this.busy(true);
        let currentKeyWord: KeyWord;
        let currentQuestion: AskedQuestion;
        let distance: number;
        let score: number;
        let filteredQuestions: Array<AskedQuestion>;
        let searchWords: Array<string> = this.searchText()
            .toLowerCase()
            .replace("_", " ")
            .replace(/[^A-Za-z0-9\s]/, "")
            .split(/\s+/)
            .filter(w => w);

        this.visibleQuestions.removeAll();

        // Reset search scores
        this.allQuestions().forEach(q => q.currentSearchScore(0));

        // Apply filters
        let categories: Category[] = this.categories().filter(c => c.selected());

        filteredQuestions = this.allQuestions().filter(q => {
            let categoryMatch: boolean = false;
            for (let i: number = 0; i < categories.length; i++) {
                for (let j: number = 0; j < q.categories().length; j++) {
                    if (categories[i].name == q.categories()[j]) {
                        categoryMatch = true;
                        break;
                    }
                }
                if (categoryMatch) {
                    break;
                }
            }
            return categoryMatch;
        });

        // Sort using the search text
        if (0 == searchWords.length) {
            filteredQuestions.slice(0, this.pageSize).forEach(q => this.visibleQuestions.push(q));
        }
        else {
            for (let i: number = 0; i < searchWords.length; i++) {
                for (let j: number = 0; j < filteredQuestions.length; j++) {
                    currentQuestion = filteredQuestions[j];
                    for (let k: number = 0; k < currentQuestion.keyWords().length; k++) {
                        currentKeyWord = currentQuestion.keyWords()[k];
                        distance = Levenshtein.get(searchWords[i], currentKeyWord.value);
                        score = (currentKeyWord.value.length - distance) / currentKeyWord.value.length;
                        if (score > 0.5) {
                            currentQuestion.currentSearchScore(currentQuestion.currentSearchScore() + (score * currentKeyWord.weight));
                        }
                    }
                }
            }
            filteredQuestions
                .sort((a, b) => b.currentSearchScore() - a.currentSearchScore())
                .slice(0, this.pageSize)
                .forEach(q => this.visibleQuestions.push(q));
        }

        this.busy(false);
    }
}

class Category {
    public readonly selected: ko.Observable<boolean> = ko.observable<boolean>(true);
    constructor(
        public readonly name: string,
        public readonly title: string,
    ) { }

    public toggleSelected(): void {
        this.selected(!this.selected());
    }
}

class AskedQuestion {

    public readonly answerBody: ko.Observable<BodyTextBindable>;
    public readonly categories: ko.ObservableArray<string>;
    public readonly keyWords: ko.ObservableArray<KeyWord>;
    public readonly questionText: ko.Observable<string>;

    public readonly isExpanded: ko.Observable<boolean> = ko.observable(false);
    public readonly currentSearchScore: ko.Observable<number> = ko.observable(0);

    constructor(questionData: FrequentlyAskedQuestion) {
        this.answerBody = ko.observable(new BodyTextBindable(questionData.answerBody));
        this.categories = ko.observableArray(questionData.categories);
        this.keyWords = ko.observableArray(questionData.keyWords);
        this.questionText = ko.observable(questionData.questionText);
    }

    public toggleIsExpanded(): void {
        this.isExpanded(!this.isExpanded());
    }
}

(new ContactAndHelp()).initialiseAsync();